import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaShoppingCart, FaUser, FaCog, FaSignOutAlt, FaTimes, FaCoins, FaCrown, FaGlobe } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { usePlayer } from '../../context/PlayerContext';
import { useTranslation, LOCALE_LABELS, LOCALE_FLAGS, Locale } from '../../i18n';
import styles from './Navbar.module.scss';

const Navbar: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const { user, isAuthenticated, logout, isAdmin } = useAuth();
  const { totalItems, toggleCart } = useCart();
  const { coinBalance, rank } = usePlayer();
  const { t, locale, setLocale } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/store', label: t('nav.store') },
    { path: '/about', label: t('nav.about') },
    { path: '/blog', label: t('nav.news') },
    { path: '/contact', label: t('nav.contact') },
    { path: '/faq', label: t('nav.faq') },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      <nav className={`${styles.navbar} ${isScrolled ? styles.scrolled : ''}`}>
        <div className={styles['navbar-container']}>
          <Link to="/" className={styles.logo}>
            <img
              src="https://cdn.silverclaw.eu/logo.png"
              alt="Logo"
              className={styles['logo-image']}
            />
          </Link>

          <div className={styles['nav-links']}>
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`${styles['nav-link']} ${isActive(link.path) ? styles.active : ''}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className={styles['nav-actions']}>
            <div className={styles['lang-selector']} ref={langRef}>
              <button
                className={styles['lang-button']}
                onClick={() => setIsLangOpen(!isLangOpen)}
                aria-label="Change language"
              >
                <span className={styles['lang-flag']}>{LOCALE_FLAGS[locale]}</span>
                <FaGlobe />
              </button>
              <div className={`${styles['lang-dropdown']} ${isLangOpen ? styles.open : ''}`}>
                {(Object.keys(LOCALE_LABELS) as Locale[]).map((loc) => (
                  <button
                    key={loc}
                    className={`${styles['lang-option']} ${loc === locale ? styles.active : ''}`}
                    onClick={() => { setLocale(loc); setIsLangOpen(false); }}
                  >
                    <span className={styles['lang-flag']}>{LOCALE_FLAGS[loc]}</span>
                    <span>{LOCALE_LABELS[loc]}</span>
                  </button>
                ))}
              </div>
            </div>

            <button
              className={styles['cart-button']}
              onClick={toggleCart}
              aria-label="Shopping cart"
            >
              <FaShoppingCart />
              {totalItems > 0 && (
                <span className={styles['cart-count']}>{totalItems}</span>
              )}
            </button>

            {isAuthenticated && user ? (
              <div className={styles['user-menu']}>
                <button
                  className={styles['user-button']}
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  <img
                    src={user.avatar || `https://mc-heads.net/avatar/${user.username}/28`}
                    alt={user.username}
                    className={styles.avatar}
                  />
                  <span className={styles.username}>{user.username}</span>
                </button>

                <div className={`${styles.dropdown} ${isDropdownOpen ? styles.open : ''}`}>
                  <div className={styles['dropdown-item']}>
                    <FaCrown style={{ color: rank?.color || '#AAAAAA' }} />
                    <span style={{ color: rank?.color }}>{rank?.displayName || t('common.player')}</span>
                  </div>
                  <div className={styles['dropdown-item']}>
                    <FaCoins style={{ color: '#D5A54C' }} />
                    <span>{coinBalance.toLocaleString()} ⛃</span>
                  </div>
                  <div className={styles['dropdown-divider']} />
                  <Link to="/dashboard" className={styles['dropdown-item']}>
                    <FaUser />
                    <span>{t('nav.dashboard')}</span>
                  </Link>
                  {isAdmin && (
                    <Link to="/admin" className={styles['dropdown-item']}>
                      <FaCrown style={{ color: '#c9a227' }} />
                      <span>Admin Panel</span>
                    </Link>
                  )}
                  <Link to="/dashboard/settings" className={styles['dropdown-item']}>
                    <FaCog />
                    <span>{t('nav.settings')}</span>
                  </Link>
                  <div className={styles['dropdown-divider']} />
                  <button className={styles['dropdown-item']} onClick={logout}>
                    <FaSignOutAlt />
                    <span>{t('nav.logout')}</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className={styles['auth-buttons']}>
                <Link to="/login" className={styles['login-btn']}>
                  {t('nav.login')}
                </Link>
              </div>
            )}

            <button
              className={`${styles['mobile-menu-toggle']} ${isMobileMenuOpen ? styles.open : ''}`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle mobile menu"
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </div>
      </nav>

      <div className={`${styles['mobile-menu']} ${isMobileMenuOpen ? styles.open : ''}`}>
        <button
          className={styles['close-menu']}
          onClick={() => setIsMobileMenuOpen(false)}
          aria-label="Close menu"
        >
          <FaTimes />
        </button>

        <div className={styles['mobile-nav-links']}>
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`${styles['mobile-nav-link']} ${isActive(link.path) ? styles.active : ''}`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        {!isAuthenticated && (
          <div className={styles['mobile-auth-buttons']}>
            <Link to="/login" className={styles['login-btn']}>
              {t('nav.login')}
            </Link>
          </div>
        )}
      </div>
    </>
  );
};

export default Navbar;
